/**
 * JournalArticlesContext
 *
 * Provides caching for journal articles data so that navigating away from
 * the articles list (e.g., to a detail page) and returning does not trigger
 * a full reload. The context stores articles, pagination state, filters,
 * and view settings.
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react"
import { JournalArticle, getJournalArticles, extractionStatusLabelMap, topicLabelMap, authorContactStatusLabelMap } from "@/services/journalArticlesService"
import {
  BiblioCollection,
  getCollections,
  COLLECTION_TYPE_TOPIC_KEY_MAP,
} from "@/services/biblioService"
import { useAuth } from "@/hooks/useAuth"
import { SidebarOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { GenericSortConfig } from "@/components/survey-catalogue/SortableColumnHeader"
import { ViewMode } from "@/components/shared/TableViewSelector"
import { getColumnGroupsForViewMode } from "@/components/journal-articles/columns"

/** Sortable column key type for journal articles. */
export type JournalArticleSortableKey = "publicationYear" | "scorePRTopic"

/**
 * The mutually-exclusive quick filters shown as toggle buttons on the PR Bulk
 * Review view. Each narrows the base "scorePRTopic exists" set further:
 *   - "all":           no extra filter beyond scorePRTopic existing.
 *   - "needs-rereview": scorePRTopic > 0.5 AND coversPRTopic === false.
 *   - "new-finds":      scorePRTopic > 0.5 AND coversPRTopic has no value.
 */
export type PrBulkReviewFilter = "all" | "needs-rereview" | "new-finds"

/**
 * The threshold a PR topic score must exceed for the Needs Rereview / New Finds
 * tabs. These tabs always pin scorePRTopic > 0.5, so this value also serves as
 * the lower bound (floor) of the PR Bulk Review score slider on those tabs.
 */
export const PR_BULK_REVIEW_SCORE_THRESHOLD: number = 0.5

/** Default page size for article pagination. */
const DEFAULT_PAGE_SIZE = 50

/**
 * Reserved sentinel id used by the biblio-collections table to store the
 * atomic collection-id counter (its item holds `currentValue`, not a real
 * collection). It must be excluded from the collections shown in the UI.
 */
const COLLECTIONS_COUNTER_ID: number = -1

/** Map of sidebar options to their corresponding API topic keys. */
const SIDEBAR_TOPIC_API_MAP: Partial<Record<SidebarOption, string>> = {
  "topic-parasite-rate": "pr",
  "topic-parasite-incidence": "api",
  "topic-insecticide-resistance": "ir",
  "topic-seasonality": "seasonality",
}

/**
 * Shape of the cached state for journal articles.
 */
interface JournalArticlesCacheState {
  /** The list of articles currently loaded. */
  articles: JournalArticle[]
  /** Total number of articles matching the current filters. */
  totalCount: number
  /** Total number of pages available. */
  totalPages: number
  /** Current page index (0-based). */
  pageIndex: number
  /** Number of items per page. */
  pageSize: number
  /** Current sidebar selection (determines which articles to show). */
  sidebarOption: SidebarOption
  /** Active PR Bulk Review quick filter (only meaningful on that view). */
  prBulkReviewFilter: PrBulkReviewFilter
  /**
   * Minimum PR topic score selected on the PR Bulk Review slider (only
   * meaningful on that view). Articles are filtered to scorePRTopic strictly
   * greater than this value. 0 means the slider imposes no extra floor.
   */
  prScoreMin: number
  /** Current search query string. */
  searchQuery: string
  /** Active table filters (key → set of selected values). */
  activeFilters: Map<string, Set<string>>
  /** Current sort configuration. */
  sortConfig: GenericSortConfig<JournalArticleSortableKey> | null
  /** Current view mode (compact, status, full). */
  viewMode: ViewMode
  /** Set of visible column keys. */
  visibleColumns: Set<string>
  /** Whether data is currently being fetched. */
  loading: boolean
  /** Error message if the last fetch failed. */
  error: string | null
  /** Timestamp of the last successful fetch (for cache invalidation). */
  lastFetchTime: number | null
}

/**
 * Context value shape with state and actions.
 */
export interface JournalArticlesContextValue extends JournalArticlesCacheState {
  /** List of biblio collections (for filter dropdowns). */
  collections: BiblioCollection[]
  /** Whether collections are still loading. */
  collectionsLoading: boolean
  /** Currently selected article (for side panel). */
  selectedArticle: JournalArticle | null
  /** ID of the currently selected article. */
  selectedArticleId: number | null
  /** Fetch a page of articles with the current filters. */
  fetchPage: (page: number) => Promise<void>
  /** Set the current page index. */
  setPageIndex: (index: number) => void
  /** Set the page size. */
  setPageSize: (size: number) => void
  /** Set the sidebar option (triggers refetch). */
  setSidebarOption: (option: SidebarOption) => void
  /** Set the PR Bulk Review quick filter (triggers refetch). */
  setPrBulkReviewFilter: (filter: PrBulkReviewFilter) => void
  /** Set the minimum PR topic score from the slider (triggers refetch). */
  setPrScoreMin: (min: number) => void
  /** Set the search query (triggers refetch). */
  setSearchQuery: (query: string) => void
  /** Set the active filters (triggers refetch). */
  setActiveFilters: (filters: Map<string, Set<string>>) => void
  /** Set the sort configuration (triggers refetch). */
  setSortConfig: (config: GenericSortConfig<JournalArticleSortableKey> | null) => void
  /** Toggle sort for a given column key. */
  toggleSort: (key: JournalArticleSortableKey) => void
  /** Set the view mode. */
  setViewMode: (mode: ViewMode) => void
  /** Set the visible columns. */
  setVisibleColumns: (columns: Set<string>) => void
  /** Select an article (shows side panel). */
  selectArticle: (article: JournalArticle) => void
  /** Clear the selected article (closes side panel). */
  clearSelectedArticle: () => void
  /** Whether sorting is enabled for the current sidebar option. */
  sortEnabled: boolean
  /** Force a refresh of the current page (bypasses cache). */
  refreshCurrentPage: () => Promise<void>
  /** Fetch collections from the API. */
  fetchCollections: () => Promise<void>
  /** Update an article in the cached list. */
  updateArticleInList: (updated: JournalArticle) => void
}

const JournalArticlesContext = createContext<JournalArticlesContextValue | null>(null)

/**
 * Props for the JournalArticlesProvider component.
 */
interface JournalArticlesProviderProps {
  /** Child components that can access the context. */
  children: ReactNode
  /** Initial sidebar option (derived from URL path). */
  initialSidebarOption?: SidebarOption
  /** Initial search query (from URL params). */
  initialSearchQuery?: string
  /** Initial page index (from URL params). */
  initialPageIndex?: number
  /** Initial page size (from URL params). */
  initialPageSize?: number
  /** Initial view mode (from URL params). */
  initialViewMode?: ViewMode
  /** Initial visible columns (from URL params). */
  initialVisibleColumns?: Set<string>
  /** Initial active filters (from URL params). */
  initialActiveFilters?: Map<string, Set<string>>
  /** Initial PR Bulk Review quick filter (from URL params). */
  initialPrBulkReviewFilter?: PrBulkReviewFilter
  /** Initial minimum PR topic score for the slider (from URL params). */
  initialPrScoreMin?: number
}

/**
 * Provider component that manages journal articles state and caching.
 *
 * Wraps components that need access to journal articles data. Caches the
 * articles list so navigating to a detail page and back does not re-fetch.
 *
 * @param props - Provider props including children and initial state values.
 * @returns The provider component wrapping children.
 */
export function JournalArticlesProvider({
  children,
  initialSidebarOption = "all-journal-articles",
  initialSearchQuery = "",
  initialPageIndex = 0,
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialViewMode = "compact",
  initialVisibleColumns,
  initialActiveFilters,
  initialPrBulkReviewFilter = "all",
  initialPrScoreMin = 0,
}: JournalArticlesProviderProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Core data state
  const [articles, setArticles] = useState<JournalArticle[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null)

  // Pagination state
  const [pageIndex, setPageIndex] = useState<number>(initialPageIndex)
  const [pageSize, setPageSize] = useState<number>(initialPageSize)

  // Filter and view state
  const [sidebarOption, setSidebarOptionState] = useState<SidebarOption>(initialSidebarOption)
  const [prBulkReviewFilter, setPrBulkReviewFilterState] =
    useState<PrBulkReviewFilter>(initialPrBulkReviewFilter)
  const [prScoreMin, setPrScoreMinState] = useState<number>(initialPrScoreMin)
  const [searchQuery, setSearchQueryState] = useState<string>(initialSearchQuery)
  const [activeFilters, setActiveFiltersState] = useState<Map<string, Set<string>>>(
    initialActiveFilters ?? new Map()
  )
  const [sortConfig, setSortConfigState] = useState<GenericSortConfig<JournalArticleSortableKey> | null>(null)
  const [viewMode, setViewModeState] = useState<ViewMode>(initialViewMode)
  const [visibleColumns, setVisibleColumnsState] = useState<Set<string>>(
    initialVisibleColumns ?? new Set(getColumnGroupsForViewMode(initialViewMode).flatMap((g) => g.keys))
  )

  // Selection state
  const [selectedArticle, setSelectedArticle] = useState<JournalArticle | null>(null)
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null)

  // Mirror of selectedArticleId kept in a ref so fetchPage can read the latest
  // value WITHOUT listing it as a dependency. If fetchPage depended on
  // selectedArticleId, selecting an article (which sets the id) would recreate
  // fetchPage, and the interface's fetch effect — which depends on fetchPage —
  // would re-run and reload the whole list. The ref breaks that chain.
  const selectedArticleIdRef = useRef<number | null>(null)
  useEffect(() => {
    selectedArticleIdRef.current = selectedArticleId
  }, [selectedArticleId])

  // Collections state
  const [collections, setCollections] = useState<BiblioCollection[]>([])
  const [collectionsLoading, setCollectionsLoading] = useState<boolean>(true)

  // Sorting is disabled for "All Journal Articles" (times out on large datasets)
  const sortEnabled: boolean = sidebarOption !== "all-journal-articles"

  // Fetch ID ref to discard stale responses
  const fetchIdRef = useRef<number>(0)

  /**
   * Reverse-lookup a label map to find API keys from selected labels.
   *
   * @param selectedLabels - Set of human-readable labels the user selected.
   * @param labelMap - The label map (API value → display label) to reverse.
   * @returns Comma-separated API values, or undefined if nothing selected.
   */
  const resolveFilterValues = useCallback(
    (selectedLabels: Set<string> | undefined, labelMap: Record<string, string>): string | undefined => {
      if (!selectedLabels?.size) return undefined
      const reversed = Object.entries(labelMap).reduce<Record<string, string>>((acc, [key, label]) => {
        acc[label] = key
        return acc
      }, {})
      const apiValues = [...selectedLabels].map((label) => reversed[label]).filter(Boolean)
      return apiValues.length > 0 ? apiValues.join(",") : undefined
    },
    []
  )

  /**
   * Join a raw filter value set into a comma-separated string for the API.
   *
   * Unlike {@link resolveFilterValues}, this performs no label-map reversal: the
   * selected values are already the exact strings the backend expects (e.g.
   * continent names, country names, or study years). Used for the
   * continents/countries/studyYears filters on the Open Author Contacts view.
   *
   * @param selectedValues - Set of raw values the user selected, or undefined.
   * @returns Comma-separated values, or undefined when nothing is selected.
   */
  const joinRawFilterValues = useCallback(
    (selectedValues: Set<string> | undefined): string | undefined => {
      if (!selectedValues?.size) return undefined
      return [...selectedValues].join(",")
    },
    []
  )

  /**
   * Resolve a collection filter label set back to a comma-separated list of
   * collection IDs. Multiple collections can be selected; the backend unions
   * their members (OR logic), so all selected ids are forwarded.
   *
   * @param selectedLabels - Set of collection name labels the user selected.
   * @returns Comma-separated collection IDs, or undefined when none selected.
   */
  const resolveCollectionIds = useCallback(
    (selectedLabels: Set<string> | undefined): string | undefined => {
      if (!selectedLabels?.size) return undefined
      const ids = collections
        .filter((c) => selectedLabels.has(c.collectionName))
        .map((c) => c.id)
      return ids.length > 0 ? ids.join(",") : undefined
    },
    [collections]
  )

  /**
   * Fetch a page of articles from the API.
   *
   * @param page - Zero-based page index to fetch.
   */
  const fetchPage = useCallback(
    async (page: number): Promise<void> => {
      const currentFetchId = ++fetchIdRef.current

      setLoading(true)
      setError(null)

      try {
        const token = await getAccessToken()

        // Build filters from sidebar selection
        const topicKey = SIDEBAR_TOPIC_API_MAP[sidebarOption]
        const collectionMatch = sidebarOption.match(/^collection-(\d+)$/)
        const selectedCollectionId = collectionMatch
          ? parseInt(collectionMatch[1], 10)
          : undefined

        // When a collection is selected, restrict its articles to the topic
        // that matches the collection's type (e.g. a "PR Literature Review"
        // collection only shows articles with coversPRTopic = true). Collection
        // types not present in the map impose no topic filter.
        const selectedCollection =
          selectedCollectionId !== undefined
            ? collections.find(collection => collection.id === selectedCollectionId)
            : undefined
        const collectionTopicKey = selectedCollection
          ? COLLECTION_TYPE_TOPIC_KEY_MAP[selectedCollection.collectionType]
          : undefined

        // PR Bulk Review view: the base filter is simply "scorePRTopic exists".
        // The active quick-filter toggle narrows that further. No topic/status
        // filters are forced here — existence of a classifier score is the only
        // base constraint (sourceType is already pinned to "article" by the GSI
        // the backend queries, so no sourceType filter is needed).
        const isPrBulkReview: boolean = sidebarOption === "pr-bulk-review"

        // The Needs Rereview / New Finds tabs always pin scorePRTopic > 0.5, so
        // that threshold is the slider's floor on those tabs; the "All" tab has
        // no inherent floor (0). The slider only ever raises the floor, so the
        // effective lower bound sent to the API is the larger of the tab floor
        // and the slider's selected minimum. A value of 0 is omitted entirely
        // so the "All" tab at rest imposes no score threshold.
        const tabScoreFloor: number =
          prBulkReviewFilter === "needs-rereview" || prBulkReviewFilter === "new-finds"
            ? PR_BULK_REVIEW_SCORE_THRESHOLD
            : 0
        const effectiveScoreMin: number = Math.max(tabScoreFloor, prScoreMin)

        const prBulkReviewFilters: Pick<
          Parameters<typeof getJournalArticles>[3] & object,
          "scorePRTopicExists" | "scorePRTopicGt" | "coversPRTopicIs"
        > = isPrBulkReview
          ? {
              scorePRTopicExists: true,
              ...(effectiveScoreMin > 0 ? { scorePRTopicGt: effectiveScoreMin } : {}),
              ...(prBulkReviewFilter === "needs-rereview"
                ? { coversPRTopicIs: "false" as const }
                : {}),
              ...(prBulkReviewFilter === "new-finds"
                ? { coversPRTopicIs: "missing" as const }
                : {}),
            }
          : {}

        const data = await getJournalArticles(token, page + 1, pageSize, {
          topic:
            collectionTopicKey ??
            topicKey ??
            (sidebarOption === "open-author-contacts" ? "pr" : undefined),
          ...prBulkReviewFilters,
          collectionId: selectedCollectionId,
          searchTerm: searchQuery || undefined,
          createdAfter:
            sidebarOption === "recently-added"
              ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
              : undefined,
          prExtractionStatus:
            sidebarOption === "open-author-contacts"
              ? "IN_PROGRESS,EXTRACTED,EDITING,EXTRACTED_VIA_COMMS"
              : resolveFilterValues(activeFilters.get("prExtractionStatus"), extractionStatusLabelMap),
          prUploadStatus: sidebarOption === "open-author-contacts" ? "NONE,PENDING_UPLOAD" : undefined,
          ...(!collectionTopicKey && !topicKey && sidebarOption !== "open-author-contacts"
            ? { topic: resolveFilterValues(activeFilters.get("topics"), topicLabelMap) }
            : {}),
          ...(!collectionMatch ? { collectionId: resolveCollectionIds(activeFilters.get("collectionIds")) } : {}),
          rsExtractionStatus: resolveFilterValues(activeFilters.get("rsExtractionStatus"), extractionStatusLabelMap),
          prContactStatus: resolveFilterValues(activeFilters.get("prContactStatus"), authorContactStatusLabelMap),
          // Continents / Countries / Study Years are only offered on the Open
          // Author Contacts view (see OpenAuthorContactsPage / the filter option
          // builders in pages/journalArticlesFilterOptions.ts),
          // so they are only forwarded there. Their selected values are the
          // exact strings the backend expects, so no label-map reversal is
          // applied — they are joined verbatim into a comma-separated list.
          ...(sidebarOption === "open-author-contacts"
            ? {
                continents: joinRawFilterValues(activeFilters.get("continents")),
                countries: joinRawFilterValues(activeFilters.get("countries")),
                studyYears: joinRawFilterValues(activeFilters.get("studyYears")),
              }
            : {}),
          sortBy: sortConfig?.key ?? undefined,
          sortDirection: sortConfig?.direction ?? undefined,
        })

        // Discard stale responses
        if (currentFetchId !== fetchIdRef.current) {
          return
        }

        setArticles(data.items)
        setTotalCount(data.totalCount)
        setTotalPages(data.totalPages)
        setLastFetchTime(Date.now())

        // If a selected article ID exists, try to resolve it from the loaded
        // data. Read from the ref (not the state value) so fetchPage does not
        // need selectedArticleId as a dependency — see the ref declaration above.
        const currentSelectedId = selectedArticleIdRef.current
        if (currentSelectedId !== null) {
          const match = data.items.find((a) => a.sourceId === currentSelectedId)
          setSelectedArticle(match ?? null)
        }
      } catch (err) {
        if (currentFetchId !== fetchIdRef.current) {
          return
        }
        setError(err instanceof Error ? err.message : "Failed to load journal articles")
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false)
        }
      }
    },
    [
      getAccessToken,
      sidebarOption,
      prBulkReviewFilter,
      prScoreMin,
      searchQuery,
      pageSize,
      activeFilters,
      sortConfig,
      resolveFilterValues,
      joinRawFilterValues,
      resolveCollectionIds,
      collections,
    ]
  )

  /**
   * Fetch collections from the API (called once on mount).
   */
  const fetchCollections = useCallback(async (): Promise<void> => {
    try {
      const token = await getAccessToken()
      const data = await getCollections(token)
      // Exclude the reserved atomic-counter row (id = -1); it is not a real
      // collection and has no collectionName, so it must never reach the UI.
      const realCollections = data.items.filter(
        collection => collection.id !== COLLECTIONS_COUNTER_ID
      )
      setCollections(realCollections)
    } catch (err) {
      console.error("Failed to fetch collections:", err)
    } finally {
      setCollectionsLoading(false)
    }
  }, [getAccessToken])

  // Load collections on mount
  useEffect(() => {
    fetchCollections()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Set the sidebar option and reset to first page.
   *
   * @param option - The new sidebar option.
   */
  const setSidebarOption = useCallback((option: SidebarOption): void => {
    setSidebarOptionState(option)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
    setSearchQueryState("")
    // Clear sort if switching to all-journal-articles
    if (option === "all-journal-articles") {
      setSortConfigState(null)
    }
  }, [])

  /**
   * Set the PR Bulk Review quick filter and reset to first page.
   *
   * Mirrors the other input setters: changing the filter resets pagination and
   * clears any open side panel so the refetch (driven by the unified fetch
   * effect) starts from a clean page-one state. The score slider is also reset
   * to the new tab's default floor — 0.5 on "Needs Rereview" / "New Finds"
   * (which always pin scorePRTopic > 0.5) and 0 on "All" — so switching tabs
   * never carries a previously-dragged threshold across to the new tab.
   *
   * @param filter - The new PR Bulk Review quick filter.
   */
  const setPrBulkReviewFilter = useCallback((filter: PrBulkReviewFilter): void => {
    setPrBulkReviewFilterState(filter)
    const tabScoreFloor: number =
      filter === "needs-rereview" || filter === "new-finds"
        ? PR_BULK_REVIEW_SCORE_THRESHOLD
        : 0
    setPrScoreMinState(tabScoreFloor)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the minimum PR topic score (from the slider) and reset to first page.
   *
   * Mirrors the other input setters: changing the slider resets pagination and
   * clears any open side panel so the refetch (driven by the unified fetch
   * effect) starts from a clean page-one state.
   *
   * @param min - The new minimum PR topic score (0 means no slider floor).
   */
  const setPrScoreMin = useCallback((min: number): void => {
    setPrScoreMinState(min)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the search query and reset to first page.
   *
   * @param query - The new search query.
   */
  const setSearchQuery = useCallback((query: string): void => {
    setSearchQueryState(query)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the active filters and reset to first page.
   *
   * @param filters - The new filter map.
   */
  const setActiveFilters = useCallback((filters: Map<string, Set<string>>): void => {
    setActiveFiltersState(filters)
    setPageIndex(0)
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Set the sort configuration and reset to first page.
   *
   * @param config - The new sort config or null to clear.
   */
  const setSortConfig = useCallback((config: GenericSortConfig<JournalArticleSortableKey> | null): void => {
    setSortConfigState(config)
    setPageIndex(0)
  }, [])

  /**
   * Toggle sort for a given column key.
   * Cycles: asc → desc → no sort (for same column), or starts at asc for new column.
   *
   * @param key - The column key to sort by.
   */
  const toggleSort = useCallback((key: JournalArticleSortableKey): void => {
    setSortConfigState((prev): GenericSortConfig<JournalArticleSortableKey> | null => {
      if (prev?.key !== key) {
        return { key, direction: "asc" }
      }
      if (prev.direction === "asc") {
        return { key, direction: "desc" }
      }
      return null
    })
    setPageIndex(0)
  }, [])

  /**
   * Set the view mode.
   *
   * @param mode - The new view mode.
   */
  const setViewMode = useCallback((mode: ViewMode): void => {
    setViewModeState(mode)
  }, [])

  /**
   * Set the visible columns.
   *
   * @param columns - The new set of visible column keys.
   */
  const setVisibleColumns = useCallback((columns: Set<string>): void => {
    setVisibleColumnsState(columns)
  }, [])

  /**
   * Select an article to view in the side panel.
   *
   * @param article - The article to select.
   */
  const selectArticle = useCallback((article: JournalArticle): void => {
    setSelectedArticle(article)
    setSelectedArticleId(article.sourceId)
  }, [])

  /**
   * Clear the selected article (closes the side panel).
   */
  const clearSelectedArticle = useCallback((): void => {
    setSelectedArticle(null)
    setSelectedArticleId(null)
  }, [])

  /**
   * Force a refresh of the current page.
   */
  const refreshCurrentPage = useCallback(async (): Promise<void> => {
    await fetchPage(pageIndex)
  }, [fetchPage, pageIndex])

  /**
   * Update an article in the cached list.
   *
   * @param updated - The updated article object.
   */
  const updateArticleInList = useCallback((updated: JournalArticle): void => {
    setArticles((prev) => prev.map((a) => (a.sourceId === updated.sourceId ? updated : a)))
    // Also update selected article if it's the same one
    if (selectedArticleId === updated.sourceId) {
      setSelectedArticle(updated)
    }
  }, [selectedArticleId])

  const value: JournalArticlesContextValue = {
    // State
    articles,
    totalCount,
    totalPages,
    pageIndex,
    pageSize,
    sidebarOption,
    prBulkReviewFilter,
    prScoreMin,
    searchQuery,
    activeFilters,
    sortConfig,
    viewMode,
    visibleColumns,
    loading,
    error,
    lastFetchTime,
    collections,
    collectionsLoading,
    selectedArticle,
    selectedArticleId,
    sortEnabled,
    // Actions
    fetchPage,
    setPageIndex,
    setPageSize,
    setSidebarOption,
    setPrBulkReviewFilter,
    setPrScoreMin,
    setSearchQuery,
    setActiveFilters,
    setSortConfig,
    toggleSort,
    setViewMode,
    setVisibleColumns,
    selectArticle,
    clearSelectedArticle,
    refreshCurrentPage,
    fetchCollections,
    updateArticleInList,
  }

  return <JournalArticlesContext.Provider value={value}>{children}</JournalArticlesContext.Provider>
}

/**
 * Hook to access the journal articles context.
 *
 * @returns The journal articles context value.
 * @throws Error if used outside of a JournalArticlesProvider.
 */
export function useJournalArticles(): JournalArticlesContextValue {
  const context = useContext(JournalArticlesContext)
  if (!context) {
    throw new Error("useJournalArticles must be used within a JournalArticlesProvider")
  }
  return context
}
